import { Box, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FormEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';

import { EmploymentRiskSectorSelection } from '@/components/Form/Employment/EmploymentRiskSectorSelection';
import EmploymentRiskSectorQuestion from '@/components/Form/Employment/EmploymentRiskyQuestion';
import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { RadioToggle } from '@/components/Form/RadioToggle';
import { Select } from '@/components/Form/Select';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import { selectUserData } from '@/redux/user-data';

import { EmploymentStatus } from '@/types/user';

import { getProfessionQuestionForSelfEmployed } from '@/utils/employment';
import { isGuyanaUser } from '@/utils/user';

import { EmploymentInfoForm } from '../helper';

import {
  QuestionsContainerItem,
  QuestionsContainerItemYesNoContainer,
} from '../../QuestionsDrawer/QuestionAboutMultipleMonthlyDeposit.styled';
import {
  QuestionLabel,
  SelfEmployedFormContainer,
} from '../EmploymentInfo.styled';

interface SelfEmployedFormProps {
  onSubmit: FormEventHandler<HTMLDivElement>;
}

export const SelfEmployedForm = ({ onSubmit }: SelfEmployedFormProps) => {
  const theme = useTheme();
  const { user } = useAppSelector(selectUserData);
  const { showSelfEmployedWithRiskSectorFlagQuestion } =
    useAppSelector(selectConfigsState);
  const { t, country } = useTranslate();
  const {
    control,
    formState: { errors: visibleErrors, isValid },
  } = useFormContext<EmploymentInfoForm>();

  const isGuyana = isGuyanaUser(user);

  return (
    <SelfEmployedFormContainer onSubmit={onSubmit}>
      <Typography variant="h3" textAlign="center">
        {t('selfEmployedForm.title')}
      </Typography>
      <QuestionsContainerItem>
        <Box mb={2}>
          <QuestionLabel>
            {t(
              'updatePersonalInfo.employmentInfoForm.field.selfEmploymentType.label',
            )}
          </QuestionLabel>
          <Select
            options={getProfessionQuestionForSelfEmployed(
              user?.tenant,
            ).options.map(item => ({
              value: item.value,
              text: t(item.text ?? ''),
            }))}
            control={control}
            name="employmentDetailed.employmentDesc"
            error={!!visibleErrors.employmentDetailed?.employmentDesc}
            rules={{
              required: t(
                'updatePersonalInfo.employmentInfoForm.field.selfEmploymentType.error.required',
              ),
            }}
            fullWidth
            useDrawerOnMobile
            withSearchBar
            sx={{ backgroundColor: theme.palette.common.white }}
          />
        </Box>

        <QuestionLabel>{t('startUpQuestion.question')}</QuestionLabel>
        <QuestionsContainerItemYesNoContainer>
          <RadioToggle
            control={control}
            name={`isStartup`}
            sx={{
              '& .MuiFormControlLabel-root': {
                marginRight: 0,
                flex: 1,
              },
            }}
            options={[
              { value: true, label: t('common.yes') },
              { value: false, label: t('common.no') },
            ]}
            rules={{
              required: t('questionDrawer.accountPurpose.accountType.required'),
            }}
          />
        </QuestionsContainerItemYesNoContainer>

        {showSelfEmployedWithRiskSectorFlagQuestion && !isGuyana && (
          <Box mt={3}>
            <EmploymentRiskSectorQuestion
              control={control}
              name="riskSectorFlag"
              employmentStatus={EmploymentStatus.SelfEmployed}
              sx={{
                gap: 3,
                padding: 0,
                '.question-options-container': {
                  padding: 0.5,
                },
                '& .MuiFormGroup-root': {
                  gap: 3,
                },
                '& .MuiFormControlLabel-root': {
                  flex: 1,
                  margin: 0,
                  alignItems: 'center',
                },
              }}
              options={[
                { value: true, label: t('common.yes') },
                { value: false, label: t('common.no') },
              ]}
            />
          </Box>
        )}
        {showSelfEmployedWithRiskSectorFlagQuestion && isGuyana && (
          <Box mt={3}>
            <EmploymentRiskSectorSelection
              control={control}
              name="employmentDetailed.riskSectorCategory"
              employmentStatus={EmploymentStatus.SelfEmployed}
              sx={{
                '& .label': {
                  ...theme.typography.body1,
                },
                '& .select-container': {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </Box>
        )}

        <Box mt={3} mb={2}>
          <QuestionLabel>
            {t('selfEmployedForm.form.employerCountry.question')}
          </QuestionLabel>
          <Select
            options={COUNTRIES_WITH_POPULAR.map(item => ({
              ...item,
              text: country[item.value],
            }))}
            control={control}
            name="employmentDetailed.employerCountry"
            error={!!visibleErrors.employmentDetailed?.employerCountry}
            rules={{
              required: t('selfEmployedForm.form.employerCountry.required'),
            }}
            fullWidth
            label={t('selfEmployedForm.form.employerCountry.label')}
            useDrawerOnMobile
            withSearchBar
            sx={{ backgroundColor: theme.palette.common.white }}
          />
        </Box>
      </QuestionsContainerItem>
      <Button variant="outlined" type="submit" fullWidth disabled={!isValid}>
        {t('questionDrawer.connection.button.proceed')}
      </Button>
      <FormErrorLogger formName="SelfEmployedForm" control={control} />
    </SelfEmployedFormContainer>
  );
};
